import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "../hooks/use-toast";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  ru: { formTitle: "Получить персональный план", formSub: "Оставьте контакты — наш эксперт пришлёт подробные рекомендации по вашему результату", name: "Имя", email: "Email", phone: "WhatsApp / Телефон", consent: "Хочу получить персональный подбор университета или школы", submit: "Отправить", thanks: "Спасибо! Наш эксперт свяжется с вами в течение 24 часов." },
  uk: { formTitle: "Отримати персональний план", formSub: "Залиште контакти — наш експерт надішле детальні рекомендації за вашим результатом", name: "Ім'я", email: "Email", phone: "WhatsApp / Телефон", consent: "Хочу отримати персональний підбір університету або школи", submit: "Надіслати", thanks: "Дякуємо! Наш експерт зв'яжеться з вами протягом 24 годин." },
  en: { formTitle: "Get your personal plan", formSub: "Leave your contacts — our expert will send detailed recommendations based on your result", name: "Name", email: "Email", phone: "WhatsApp / Phone", consent: "I want a personal university or school recommendation", submit: "Submit", thanks: "Thank you! Our expert will contact you within 24 hours." },
};

const TestPage = () => {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.uk;
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(1000);
  const [testResult, setTestResult] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "", consent: true });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.type === "studygri-test-resize" && typeof e.data.height === "number") {
        setIframeHeight((prev) => {
          const next = e.data.height + 8;
          return Math.abs(next - prev) < 16 ? prev : next;
        });
      }
      if (e.data.type === "studygri-test-complete") {
        setTestResult({ country: e.data.country, program: e.data.program, flag: e.data.flag });
        setTimeout(() => document.getElementById("test-lead-form")?.scrollIntoView({ behavior: "smooth" }), 600);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Sync language to iframe
  useEffect(() => {
    const send = () => {
      try { iframeRef.current?.contentWindow?.postMessage({ type: "studygri-set-lang", lang }, "*"); } catch (e) { /* noop */ }
    };
    const t1 = setTimeout(send, 800);
    return () => clearTimeout(t1);
  }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) {
      toast({ title: lang === "en" ? "Please fill in all fields" : "Заполните все поля", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const country = testResult?.country || "";
      const program = testResult?.program || "Career Test";
      await axios.post(`${API}/leads`, {
        name: f.name,
        phone: f.phone,
        email: f.email,
        country,
        program,
        message: `Career test result: ${testResult?.flag || ""} ${country} · ${program}. Wants recommendation: ${f.consent ? "yes" : "no"}`,
        lang,
        source: "career-test",
      });
      setSent(true);
    } catch {
      toast({ title: lang === "en" ? "Error" : "Ошибка", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAF8F5" }} data-testid="test-page">
      <Header />
      <main className="pt-28 md:pt-36">
        <iframe
          ref={iframeRef}
          src="/test-quiz.html"
          title="STUDYGRI Career Test"
          style={{ width: "100%", height: `${iframeHeight}px`, border: 0, display: "block" }}
          data-testid="test-iframe"
        />

        {/* LEAD FORM */}
        <section id="test-lead-form" className="py-16 md:py-20 px-5 md:px-8" style={{ background: "#1B2A4A" }}>
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: "#C9A96E" }}>STUDYGRI</p>
              <h2 className="font-display text-3xl md:text-4xl text-white" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>{t.formTitle}</h2>
              <p className="mt-3 text-sm md:text-base" style={{ color: "rgba(245,239,230,0.65)" }}>{t.formSub}</p>
              {testResult && (
                <p className="mt-4 inline-block px-4 py-2 rounded-full text-sm font-semibold" style={{ background: "rgba(201,169,110,0.15)", color: "#E8D5B0", border: "1px solid rgba(201,169,110,0.4)" }}>
                  {testResult.flag} {testResult.country} · {testResult.program}
                </p>
              )}
            </div>

            {sent ? (
              <div className="rounded-2xl p-7 text-center" style={{ background: "rgba(201,169,110,0.12)", border: "1px solid #C9A96E" }} data-testid="test-form-sent">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#C9A96E" }} />
                <p className="text-white font-semibold text-base">{t.thanks}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="test-form">
                <div>
                  <Label className="text-xs text-white/70">{t.name} *</Label>
                  <Input value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} className="mt-1 bg-white" data-testid="tf-name" />
                </div>
                <div>
                  <Label className="text-xs text-white/70">{t.email} *</Label>
                  <Input type="email" value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} className="mt-1 bg-white" data-testid="tf-email" />
                </div>
                <div>
                  <Label className="text-xs text-white/70">{t.phone} *</Label>
                  <Input value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))} placeholder="+380..." className="mt-1 bg-white" data-testid="tf-phone" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer pt-1">
                  <Checkbox checked={f.consent} onCheckedChange={(v) => setF((p) => ({ ...p, consent: !!v }))} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#C9A96E] data-[state=checked]:border-[#C9A96E]" data-testid="tf-consent" />
                  <span className="text-sm text-white/80 leading-snug">{t.consent}</span>
                </label>
                <button type="submit" disabled={busy} className="w-full h-12 rounded-full font-bold inline-flex items-center justify-center gap-2 transition hover:opacity-90" style={{ background: "#C9A96E", color: "#1B2A4A" }} data-testid="tf-submit">
                  {busy ? "..." : t.submit} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TestPage;
